import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck, Settings2 } from "lucide-react";
import api from "@/lib/api";
import { Spinner, EmptyState } from "@/components/Bits";
import { Switch } from "@/components/ui/switch";

const timeAgo = (iso) => {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};
const ICON = { follow: "🫂", favorite: "⭐", like: "❤️", comment: "💬", download: "⬇️", friend_request: "🤝", friend_accept: "✅", collab_invite: "🧑‍🤝‍🧑", message: "💌", sale: "💰", publish: "🚀", ai_complete: "✨", reward: "🏆", guild_pro: "🛡️", remix: "🔀", takedown: "🛡️", models_ready: "🎨", vibe_reply: "💬", taunt: "🔥" };

export default function Notifications() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [showPrefs, setShowPrefs] = useState(false);
  const [prefs, setPrefs] = useState(null);

  const load = async () => { try { const { data } = await api.get("/notifications", { params: { limit: 50 } }); setData(data); } catch { setData({ notifications: [], unread: 0 }); } };
  useEffect(() => { load(); }, []);
  useEffect(() => { api.get("/notifications/preferences").then((r) => setPrefs(r.data)).catch(() => {}); }, []);

  const markAll = async () => { await api.post("/notifications/read", {}); load(); };
  const click = async (n) => {
    if (!n.read) await api.post("/notifications/read", { ids: [n.id] }).catch(() => {});
    if (n.link) navigate(n.link); else load();
  };
  const togglePref = async (key, val) => {
    setPrefs((p) => ({ ...p, prefs: { ...p.prefs, [key]: val } }));
    try { const { data } = await api.patch("/notifications/preferences", { prefs: { [key]: val } }); setPrefs(data); } catch { /* ignore */ }
  };

  const list = data?.notifications || [];

  return (
    <div className="p-5 lg:p-8 max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Bell className="w-8 h-8 text-[hsl(var(--primary))]" />
          <h1 className="font-display text-3xl font-extrabold">Notifications</h1>
        </div>
        <div className="flex items-center gap-2">
          {(data?.unread || 0) > 0 && <button onClick={markAll} data-testid="page-mark-all" className="text-sm font-semibold text-[hsl(var(--primary))] flex items-center gap-1.5"><CheckCheck className="w-4 h-4" /> Mark all read</button>}
          <button onClick={() => setShowPrefs((v) => !v)} data-testid="notif-prefs-toggle" className="w-9 h-9 grid place-items-center rounded-full hover:bg-[hsl(var(--secondary))]"><Settings2 className="w-5 h-5" /></button>
        </div>
      </div>

      {showPrefs && prefs && (
        <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5 mb-6" data-testid="notif-preferences">
          <h2 className="font-bold mb-3">Notification preferences</h2>
          <div className="space-y-3">
            {prefs.types.map((t) => (
              <div key={t.key} className="flex items-center justify-between">
                <span className="text-sm flex items-center gap-2">{t.emoji} {t.label}</span>
                <Switch checked={prefs.prefs[t.key]} onCheckedChange={(v) => togglePref(t.key, v)} data-testid={`pref-${t.key}`} />
              </div>
            ))}
          </div>
        </div>
      )}

      {!data ? <Spinner /> : list.length === 0 ? (
        <EmptyState emoji="🔔" title="No notifications yet" text="When people follow, favourite, or review your games, you'll see it here." />
      ) : (
        <div className="space-y-2" data-testid="notif-list">
          {list.map((n) => (
            <button key={n.id} onClick={() => click(n)} data-testid={`notif-row-${n.id}`}
              className={`w-full text-left rounded-2xl border p-4 flex gap-3 transition-colors hover:bg-[hsl(var(--secondary))] ${!n.read ? "border-[hsl(var(--primary))]/40 bg-[hsl(var(--primary))]/5" : "border-[hsl(var(--border))]"}`}>
              <span className="text-2xl shrink-0">{n.actor?.avatar || ICON[n.type] || "🔔"}</span>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium leading-snug">{n.title}</div>
                {n.body && <div className="text-xs text-[hsl(var(--muted-foreground))] truncate">"{n.body}"</div>}
                <div className="text-[11px] text-[hsl(var(--muted-foreground))] mt-0.5">{timeAgo(n.created_at)}</div>
              </div>
              {!n.read && <span className="w-2.5 h-2.5 rounded-full bg-[hsl(var(--primary))] shrink-0 mt-1.5" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
